import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination as PaginationProps } from '../../interfaces';
import './styles.scss';

const getBdsLanguage = (language?: string) => {
  const lang = (language || 'pt-BR').toLowerCase();
  if (lang.startsWith('en')) return 'en_US';
  if (lang.startsWith('es')) return 'es_MX';
  return 'pt_BR';
};

export const Pagination = (props: PaginationProps) => {
  const { t, i18n } = useTranslation();
  const paginationRef = useRef<HTMLBdsPaginationElement>(null);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const { itemsPerPage, itemsPerPageOptions, onItemsPerPageChange } = props;
  const hasItemsPerPageSelector = !!itemsPerPageOptions?.length;
  const [orderedOptions] = useState<number[]>(() => [
    itemsPerPage,
    ...(itemsPerPageOptions ?? []).filter(option => option !== itemsPerPage),
  ]);

  const handleChangePage = useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<number>;
      if (detail) {
        props.onChange(detail);
        setSelectedPage(detail);
      }
    },
    [props],
  );

  const handleChangeItemsPerPage = useCallback(
    (event: Event) => {
      const value = Number((event as CustomEvent<number>).detail);
      if (value && value !== itemsPerPage) {
        setSelectedPage(1);
        onItemsPerPageChange?.(value);
      }
    },
    [itemsPerPage, onItemsPerPageChange],
  );

  const getTextPagination = useCallback(() => {
    const startIndex = (selectedPage - 1) * props.itemsPerPage + (props.totalItems > 0 ? 1 : 0);
    let endIndex = selectedPage * props.itemsPerPage;
    const normalizedLanguage = (i18n.resolvedLanguage || 'pt-BR').replace('_', '-');

    if (endIndex > props.totalItems) {
      endIndex = props.totalItems;
    }

    return `${startIndex}-${endIndex} ${t('go-personal-category-selection-pagination-of')} ${props.totalItems.toLocaleString(normalizedLanguage, { minimumFractionDigits: 0 })}`;
  }, [selectedPage, props.totalItems, props.itemsPerPage, t, i18n.resolvedLanguage]);

  useEffect(() => {
    const { current } = paginationRef;
    if (current) {
      current.addEventListener('bdsPaginationChange', handleChangePage);

      return () => {
        current.removeEventListener('bdsPaginationChange', handleChangePage);
      };
    }
  }, [handleChangePage]);

  useEffect(() => {
    const { current } = paginationRef;
    if (current && hasItemsPerPageSelector) {
      current.addEventListener('bdsItemsPerPageChange', handleChangeItemsPerPage);

      return () => {
        current.removeEventListener('bdsItemsPerPageChange', handleChangeItemsPerPage);
      };
    }
  }, [handleChangeItemsPerPage, hasItemsPerPageSelector]);

  if (hasItemsPerPageSelector) {
    return (
      <bds-grid align-items="center" gap="1" class="pagination">
        <bds-pagination
          ref={paginationRef}
          page-counter={true}
          items-page={JSON.stringify(orderedOptions)}
          number-items={props.totalItems}
          pages={Math.ceil(props.totalItems / itemsPerPage)}
          language={getBdsLanguage(i18n.resolvedLanguage)}
        />
      </bds-grid>
    );
  }

  return (
    <bds-grid align-items="center" gap="1" class="pagination">
      <bds-typo variant="fs-14" bold="regular">
        {getTextPagination()}
      </bds-typo>
      <bds-pagination ref={paginationRef} pages={Math.ceil(props.totalItems / props.itemsPerPage)} />
    </bds-grid>
  );
};
